/**
 * 구/군 및 랜드마크 데이터
 * 도시 하위 지역 검색 지원 (서울 25개 구 + 주요 랜드마크)
 */

import type { Locale } from "@/i18n/routing";
import type { LocalizedPlaceName } from "@/config/locations";

export interface District {
  id: string;
  cityId: string; // CITIES의 id 참조 (예: "KR-SEL")
  name: LocalizedPlaceName;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  aliases?: string[]; // 검색용 별칭 (예: ["강남", "강남역"])
}

export interface Landmark {
  id: string;
  cityId: string;
  districtId?: string;
  name: LocalizedPlaceName;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  aliases?: string[]; // 검색용 별칭
  searchRadius?: number; // 검색 반경 (km), 기본 2km
}

// ===== 서울 25개 구 =====
export const SEOUL_DISTRICTS: District[] = [
  {
    id: "KR-SEL-GN",
    cityId: "KR-SEL",
    name: {
      en: "Gangnam-gu",
      ko: "강남구",
      ja: "江南区",
      "zh-CN": "江南区",
      "zh-TW": "江南區",
    },
    coordinates: { latitude: 37.5172, longitude: 127.0473 },
    aliases: ["강남"],
  },
  {
    id: "KR-SEL-GD",
    cityId: "KR-SEL",
    name: {
      en: "Gangdong-gu",
      ko: "강동구",
      ja: "江東区",
      "zh-CN": "江东区",
      "zh-TW": "江東區",
    },
    coordinates: { latitude: 37.5301, longitude: 127.1238 },
    aliases: ["강동"],
  },
  {
    id: "KR-SEL-GB",
    cityId: "KR-SEL",
    name: {
      en: "Gangbuk-gu",
      ko: "강북구",
      ja: "江北区",
      "zh-CN": "江北区",
      "zh-TW": "江北區",
    },
    coordinates: { latitude: 37.6396, longitude: 127.0257 },
    aliases: ["강북"],
  },
  {
    id: "KR-SEL-GS",
    cityId: "KR-SEL",
    name: {
      en: "Gangseo-gu",
      ko: "강서구",
      ja: "江西区",
      "zh-CN": "江西区",
      "zh-TW": "江西區",
    },
    coordinates: { latitude: 37.5509, longitude: 126.8495 },
    aliases: ["강서"],
  },
  {
    id: "KR-SEL-GA",
    cityId: "KR-SEL",
    name: {
      en: "Gwanak-gu",
      ko: "관악구",
      ja: "冠岳区",
      "zh-CN": "冠岳区",
      "zh-TW": "冠岳區",
    },
    coordinates: { latitude: 37.4784, longitude: 126.9516 },
    aliases: ["관악"],
  },
  {
    id: "KR-SEL-GJ",
    cityId: "KR-SEL",
    name: {
      en: "Gwangjin-gu",
      ko: "광진구",
      ja: "広津区",
      "zh-CN": "广津区",
      "zh-TW": "廣津區",
    },
    coordinates: { latitude: 37.5385, longitude: 127.0823 },
    aliases: ["광진"],
  },
  {
    id: "KR-SEL-GR",
    cityId: "KR-SEL",
    name: {
      en: "Guro-gu",
      ko: "구로구",
      ja: "九老区",
      "zh-CN": "九老区",
      "zh-TW": "九老區",
    },
    coordinates: { latitude: 37.4954, longitude: 126.8874 },
    aliases: ["구로"],
  },
  {
    id: "KR-SEL-GC",
    cityId: "KR-SEL",
    name: {
      en: "Geumcheon-gu",
      ko: "금천구",
      ja: "衿川区",
      "zh-CN": "衿川区",
      "zh-TW": "衿川區",
    },
    coordinates: { latitude: 37.4519, longitude: 126.9020 },
    aliases: ["금천"],
  },
  {
    id: "KR-SEL-NW",
    cityId: "KR-SEL",
    name: {
      en: "Nowon-gu",
      ko: "노원구",
      ja: "蘆原区",
      "zh-CN": "芦原区",
      "zh-TW": "蘆原區",
    },
    coordinates: { latitude: 37.6542, longitude: 127.0568 },
    aliases: ["노원"],
  },
  {
    id: "KR-SEL-DB",
    cityId: "KR-SEL",
    name: {
      en: "Dobong-gu",
      ko: "도봉구",
      ja: "道峰区",
      "zh-CN": "道峰区",
      "zh-TW": "道峰區",
    },
    coordinates: { latitude: 37.6688, longitude: 127.0471 },
    aliases: ["도봉"],
  },
  {
    id: "KR-SEL-DDM",
    cityId: "KR-SEL",
    name: {
      en: "Dongdaemun-gu",
      ko: "동대문구",
      ja: "東大門区",
      "zh-CN": "东大门区",
      "zh-TW": "東大門區",
    },
    coordinates: { latitude: 37.5744, longitude: 127.0396 },
    aliases: ["동대문"],
  },
  {
    id: "KR-SEL-DJ",
    cityId: "KR-SEL",
    name: {
      en: "Dongjak-gu",
      ko: "동작구",
      ja: "銅雀区",
      "zh-CN": "铜雀区",
      "zh-TW": "銅雀區",
    },
    coordinates: { latitude: 37.5124, longitude: 126.9393 },
    aliases: ["동작"],
  },
  {
    id: "KR-SEL-MP",
    cityId: "KR-SEL",
    name: {
      en: "Mapo-gu",
      ko: "마포구",
      ja: "麻浦区",
      "zh-CN": "麻浦区",
      "zh-TW": "麻浦區",
    },
    coordinates: { latitude: 37.5663, longitude: 126.9014 },
    aliases: ["마포"],
  },
  {
    id: "KR-SEL-SDM",
    cityId: "KR-SEL",
    name: {
      en: "Seodaemun-gu",
      ko: "서대문구",
      ja: "西大門区",
      "zh-CN": "西大门区",
      "zh-TW": "西大門區",
    },
    coordinates: { latitude: 37.5791, longitude: 126.9368 },
    aliases: ["서대문"],
  },
  {
    id: "KR-SEL-SC",
    cityId: "KR-SEL",
    name: {
      en: "Seocho-gu",
      ko: "서초구",
      ja: "瑞草区",
      "zh-CN": "瑞草区",
      "zh-TW": "瑞草區",
    },
    coordinates: { latitude: 37.4837, longitude: 127.0324 },
    aliases: ["서초"],
  },
  {
    id: "KR-SEL-SD",
    cityId: "KR-SEL",
    name: {
      en: "Seongdong-gu",
      ko: "성동구",
      ja: "城東区",
      "zh-CN": "城东区",
      "zh-TW": "城東區",
    },
    coordinates: { latitude: 37.5633, longitude: 127.0371 },
    aliases: ["성동"],
  },
  {
    id: "KR-SEL-SB",
    cityId: "KR-SEL",
    name: {
      en: "Seongbuk-gu",
      ko: "성북구",
      ja: "城北区",
      "zh-CN": "城北区",
      "zh-TW": "城北區",
    },
    coordinates: { latitude: 37.5894, longitude: 127.0167 },
    aliases: ["성북"],
  },
  {
    id: "KR-SEL-SP",
    cityId: "KR-SEL",
    name: {
      en: "Songpa-gu",
      ko: "송파구",
      ja: "松坡区",
      "zh-CN": "松坡区",
      "zh-TW": "松坡區",
    },
    coordinates: { latitude: 37.5145, longitude: 127.1050 },
    aliases: ["송파"],
  },
  {
    id: "KR-SEL-YC",
    cityId: "KR-SEL",
    name: {
      en: "Yangcheon-gu",
      ko: "양천구",
      ja: "陽川区",
      "zh-CN": "阳川区",
      "zh-TW": "陽川區",
    },
    coordinates: { latitude: 37.5270, longitude: 126.8665 },
    aliases: ["양천"],
  },
  {
    id: "KR-SEL-YDP",
    cityId: "KR-SEL",
    name: {
      en: "Yeongdeungpo-gu",
      ko: "영등포구",
      ja: "永登浦区",
      "zh-CN": "永登浦区",
      "zh-TW": "永登浦區",
    },
    coordinates: { latitude: 37.5264, longitude: 126.8963 },
    aliases: ["영등포", "여의도"],
  },
  {
    id: "KR-SEL-YS",
    cityId: "KR-SEL",
    name: {
      en: "Yongsan-gu",
      ko: "용산구",
      ja: "龍山区",
      "zh-CN": "龙山区",
      "zh-TW": "龍山區",
    },
    coordinates: { latitude: 37.5324, longitude: 126.9900 },
    aliases: ["용산", "이태원"],
  },
  {
    id: "KR-SEL-EP",
    cityId: "KR-SEL",
    name: {
      en: "Eunpyeong-gu",
      ko: "은평구",
      ja: "恩平区",
      "zh-CN": "恩平区",
      "zh-TW": "恩平區",
    },
    coordinates: { latitude: 37.6027, longitude: 126.9291 },
    aliases: ["은평"],
  },
  {
    id: "KR-SEL-JR",
    cityId: "KR-SEL",
    name: {
      en: "Jongno-gu",
      ko: "종로구",
      ja: "鍾路区",
      "zh-CN": "钟路区",
      "zh-TW": "鍾路區",
    },
    coordinates: { latitude: 37.5735, longitude: 126.9790 },
    aliases: ["종로"],
  },
  {
    id: "KR-SEL-JG",
    cityId: "KR-SEL",
    name: {
      en: "Jung-gu",
      ko: "중구",
      ja: "中区",
      "zh-CN": "中区",
      "zh-TW": "中區",
    },
    coordinates: { latitude: 37.5636, longitude: 126.9975 },
    aliases: ["중구", "명동", "을지로"],
  },
  {
    id: "KR-SEL-JN",
    cityId: "KR-SEL",
    name: {
      en: "Jungnang-gu",
      ko: "중랑구",
      ja: "中浪区",
      "zh-CN": "中浪区",
      "zh-TW": "中浪區",
    },
    coordinates: { latitude: 37.6066, longitude: 127.0928 },
    aliases: ["중랑"],
  },
];

// ===== 서울 주요 랜드마크 =====
export const SEOUL_LANDMARKS: Landmark[] = [
  {
    id: "KR-SEL-LM-HD",
    cityId: "KR-SEL",
    districtId: "KR-SEL-MP",
    name: {
      en: "Hongdae",
      ko: "홍대",
      ja: "弘大",
      "zh-CN": "弘大",
      "zh-TW": "弘大",
    },
    coordinates: { latitude: 37.5563, longitude: 126.9236 },
    aliases: ["홍대입구", "홍익대", "홍대역"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-MD",
    cityId: "KR-SEL",
    districtId: "KR-SEL-JG",
    name: {
      en: "Myeongdong",
      ko: "명동",
      ja: "明洞",
      "zh-CN": "明洞",
      "zh-TW": "明洞",
    },
    coordinates: { latitude: 37.5636, longitude: 126.985 },
    aliases: ["명동역"],
    searchRadius: 1.0,
  },
  {
    id: "KR-SEL-LM-GNS",
    cityId: "KR-SEL",
    districtId: "KR-SEL-GN",
    name: {
      en: "Gangnam Station",
      ko: "강남역",
      ja: "江南駅",
      "zh-CN": "江南站",
      "zh-TW": "江南站",
    },
    coordinates: { latitude: 37.4979, longitude: 127.0276 },
    aliases: ["강남스타일"],
    searchRadius: 1.0,
  },
  {
    id: "KR-SEL-LM-IT",
    cityId: "KR-SEL",
    districtId: "KR-SEL-YS",
    name: {
      en: "Itaewon",
      ko: "이태원",
      ja: "梨泰院",
      "zh-CN": "梨泰院",
      "zh-TW": "梨泰院",
    },
    coordinates: { latitude: 37.5345, longitude: 126.9946 },
    aliases: ["이태원역"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-DDM",
    cityId: "KR-SEL",
    districtId: "KR-SEL-DDM",
    name: {
      en: "Dongdaemun",
      ko: "동대문",
      ja: "東大門",
      "zh-CN": "东大门",
      "zh-TW": "東大門",
    },
    coordinates: { latitude: 37.5711, longitude: 127.0091 },
    aliases: ["동대문역", "동대문시장", "DDP"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-SIN",
    cityId: "KR-SEL",
    districtId: "KR-SEL-MP",
    name: {
      en: "Sinchon",
      ko: "신촌",
      ja: "新村",
      "zh-CN": "新村",
      "zh-TW": "新村",
    },
    coordinates: { latitude: 37.555, longitude: 126.9368 },
    aliases: ["신촌역", "연세대"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-APG",
    cityId: "KR-SEL",
    districtId: "KR-SEL-GN",
    name: {
      en: "Apgujeong",
      ko: "압구정",
      ja: "狎鷗亭",
      "zh-CN": "狎鸥亭",
      "zh-TW": "狎鷗亭",
    },
    coordinates: { latitude: 37.5273, longitude: 127.0286 },
    aliases: ["압구정로데오", "청담"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-JM",
    cityId: "KR-SEL",
    districtId: "KR-SEL-SP",
    name: {
      en: "Jamsil",
      ko: "잠실",
      ja: "蚕室",
      "zh-CN": "蚕室",
      "zh-TW": "蠶室",
    },
    coordinates: { latitude: 37.5133, longitude: 127.1001 },
    aliases: ["잠실역", "롯데월드", "석촌호수"],
    searchRadius: 2.0,
  },
  {
    id: "KR-SEL-LM-YI",
    cityId: "KR-SEL",
    districtId: "KR-SEL-YDP",
    name: {
      en: "Yeouido",
      ko: "여의도",
      ja: "汝矣島",
      "zh-CN": "汝矣岛",
      "zh-TW": "汝矣島",
    },
    coordinates: { latitude: 37.5219, longitude: 126.924 },
    aliases: ["여의도역", "63빌딩", "한강공원"],
    searchRadius: 2.0,
  },
  {
    id: "KR-SEL-LM-ICF",
    cityId: "KR-SEL",
    districtId: "KR-SEL-YS",
    name: {
      en: "Ichon",
      ko: "이촌",
      ja: "二村",
      "zh-CN": "二村",
      "zh-TW": "二村",
    },
    coordinates: { latitude: 37.5216, longitude: 126.9695 },
    aliases: ["이촌역", "국립중앙박물관"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-GB",
    cityId: "KR-SEL",
    districtId: "KR-SEL-JR",
    name: {
      en: "Gyeongbokgung",
      ko: "경복궁",
      ja: "景福宮",
      "zh-CN": "景福宫",
      "zh-TW": "景福宮",
    },
    coordinates: { latitude: 37.5796, longitude: 126.977 },
    aliases: ["경복궁역", "광화문", "북촌"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-HBC",
    cityId: "KR-SEL",
    districtId: "KR-SEL-YS",
    name: {
      en: "Haebangchon",
      ko: "해방촌",
      ja: "解放村",
      "zh-CN": "解放村",
      "zh-TW": "解放村",
    },
    coordinates: { latitude: 37.5418, longitude: 126.9871 },
    aliases: ["HBC", "녹사평"],
    searchRadius: 1.0,
  },
  {
    id: "KR-SEL-LM-SM",
    cityId: "KR-SEL",
    districtId: "KR-SEL-YDP",
    name: {
      en: "Sindorim",
      ko: "신도림",
      ja: "新道林",
      "zh-CN": "新道林",
      "zh-TW": "新道林",
    },
    coordinates: { latitude: 37.5089, longitude: 126.8914 },
    aliases: ["신도림역", "디큐브시티"],
    searchRadius: 1.0,
  },
  {
    id: "KR-SEL-LM-HY",
    cityId: "KR-SEL",
    districtId: "KR-SEL-SD",
    name: {
      en: "Hanyang Univ.",
      ko: "한양대",
      ja: "漢陽大",
      "zh-CN": "汉阳大",
      "zh-TW": "漢陽大",
    },
    coordinates: { latitude: 37.5578, longitude: 127.0441 },
    aliases: ["한양대역", "왕십리"],
    searchRadius: 1.5,
  },
  {
    id: "KR-SEL-LM-GD",
    cityId: "KR-SEL",
    districtId: "KR-SEL-GJ",
    name: {
      en: "Konkuk Univ.",
      ko: "건대",
      ja: "建大",
      "zh-CN": "建大",
      "zh-TW": "建大",
    },
    coordinates: { latitude: 37.5407, longitude: 127.0704 },
    aliases: ["건대입구", "건국대", "건대역"],
    searchRadius: 1.5,
  },
];

// ===== 부산 구/군 =====
export const BUSAN_DISTRICTS: District[] = [
  {
    id: "KR-PUS-HU",
    cityId: "KR-PUS",
    name: {
      en: "Haeundae-gu",
      ko: "해운대구",
      ja: "海雲台区",
      "zh-CN": "海云台区",
      "zh-TW": "海雲台區",
    },
    coordinates: { latitude: 35.1631, longitude: 129.1635 },
    aliases: ["해운대"],
  },
  {
    id: "KR-PUS-JG",
    cityId: "KR-PUS",
    name: {
      en: "Jung-gu",
      ko: "중구",
      ja: "中区",
      "zh-CN": "中区",
      "zh-TW": "中區",
    },
    coordinates: { latitude: 35.1064, longitude: 129.032 },
    aliases: ["부산중구", "남포동", "자갈치"],
  },
  {
    id: "KR-PUS-SG",
    cityId: "KR-PUS",
    name: {
      en: "Seo-gu",
      ko: "서구",
      ja: "西区",
      "zh-CN": "西区",
      "zh-TW": "西區",
    },
    coordinates: { latitude: 35.0977, longitude: 129.0241 },
    aliases: ["부산서구"],
  },
  {
    id: "KR-PUS-DG",
    cityId: "KR-PUS",
    name: {
      en: "Dong-gu",
      ko: "동구",
      ja: "東区",
      "zh-CN": "东区",
      "zh-TW": "東區",
    },
    coordinates: { latitude: 35.1295, longitude: 129.0453 },
    aliases: ["부산동구", "부산역"],
  },
  {
    id: "KR-PUS-YS",
    cityId: "KR-PUS",
    name: {
      en: "Yeongdo-gu",
      ko: "영도구",
      ja: "影島区",
      "zh-CN": "影岛区",
      "zh-TW": "影島區",
    },
    coordinates: { latitude: 35.0912, longitude: 129.0673 },
    aliases: ["영도"],
  },
  {
    id: "KR-PUS-BJ",
    cityId: "KR-PUS",
    name: {
      en: "Busanjin-gu",
      ko: "부산진구",
      ja: "釜山鎮区",
      "zh-CN": "釜山镇区",
      "zh-TW": "釜山鎮區",
    },
    coordinates: { latitude: 35.1631, longitude: 129.0531 },
    aliases: ["부산진", "서면"],
  },
  {
    id: "KR-PUS-DRN",
    cityId: "KR-PUS",
    name: {
      en: "Dongnae-gu",
      ko: "동래구",
      ja: "東萊区",
      "zh-CN": "东莱区",
      "zh-TW": "東萊區",
    },
    coordinates: { latitude: 35.2047, longitude: 129.0841 },
    aliases: ["동래"],
  },
  {
    id: "KR-PUS-SH",
    cityId: "KR-PUS",
    name: {
      en: "Saha-gu",
      ko: "사하구",
      ja: "沙下区",
      "zh-CN": "沙下区",
      "zh-TW": "沙下區",
    },
    coordinates: { latitude: 35.1046, longitude: 128.9747 },
    aliases: ["사하", "다대포"],
  },
  {
    id: "KR-PUS-GJ",
    cityId: "KR-PUS",
    name: {
      en: "Geumjeong-gu",
      ko: "금정구",
      ja: "金井区",
      "zh-CN": "金井区",
      "zh-TW": "金井區",
    },
    coordinates: { latitude: 35.2428, longitude: 129.0922 },
    aliases: ["금정", "부산대"],
  },
  {
    id: "KR-PUS-SY",
    cityId: "KR-PUS",
    name: {
      en: "Suyeong-gu",
      ko: "수영구",
      ja: "水営区",
      "zh-CN": "水营区",
      "zh-TW": "水營區",
    },
    coordinates: { latitude: 35.1457, longitude: 129.1133 },
    aliases: ["수영", "광안리", "민락동"],
  },
];

// ===== 부산 주요 랜드마크 =====
export const BUSAN_LANDMARKS: Landmark[] = [
  {
    id: "KR-PUS-LM-HB",
    cityId: "KR-PUS",
    districtId: "KR-PUS-HU",
    name: {
      en: "Haeundae Beach",
      ko: "해운대해수욕장",
      ja: "海雲台海水浴場",
      "zh-CN": "海云台海水浴场",
      "zh-TW": "海雲台海水浴場",
    },
    coordinates: { latitude: 35.1587, longitude: 129.1604 },
    aliases: ["해운대비치", "해수욕장"],
    searchRadius: 1.5,
  },
  {
    id: "KR-PUS-LM-GA",
    cityId: "KR-PUS",
    districtId: "KR-PUS-SY",
    name: {
      en: "Gwangalli Beach",
      ko: "광안리",
      ja: "広安里",
      "zh-CN": "广安里",
      "zh-TW": "廣安里",
    },
    coordinates: { latitude: 35.1531, longitude: 129.1185 },
    aliases: ["광안리해수욕장", "광안대교"],
    searchRadius: 1.5,
  },
  {
    id: "KR-PUS-LM-SM",
    cityId: "KR-PUS",
    districtId: "KR-PUS-BJ",
    name: {
      en: "Seomyeon",
      ko: "서면",
      ja: "西面",
      "zh-CN": "西面",
      "zh-TW": "西面",
    },
    coordinates: { latitude: 35.1578, longitude: 129.0599 },
    aliases: ["서면역"],
    searchRadius: 1.5,
  },
  {
    id: "KR-PUS-LM-NP",
    cityId: "KR-PUS",
    districtId: "KR-PUS-JG",
    name: {
      en: "Nampo-dong",
      ko: "남포동",
      ja: "南浦洞",
      "zh-CN": "南浦洞",
      "zh-TW": "南浦洞",
    },
    coordinates: { latitude: 35.0984, longitude: 129.0326 },
    aliases: ["남포", "BIFF광장", "국제시장"],
    searchRadius: 1.5,
  },
];

// ===== 제주 지역 =====
export const JEJU_DISTRICTS: District[] = [
  {
    id: "KR-JEJ-JS",
    cityId: "KR-JEJ",
    name: {
      en: "Jeju-si",
      ko: "제주시",
      ja: "済州市",
      "zh-CN": "济州市",
      "zh-TW": "濟州市",
    },
    coordinates: { latitude: 33.4996, longitude: 126.5312 },
    aliases: ["제주"],
  },
  {
    id: "KR-JEJ-SG",
    cityId: "KR-JEJ",
    name: {
      en: "Seogwipo-si",
      ko: "서귀포시",
      ja: "西帰浦市",
      "zh-CN": "西归浦市",
      "zh-TW": "西歸浦市",
    },
    coordinates: { latitude: 33.2541, longitude: 126.56 },
    aliases: ["서귀포"],
  },
];

// ===== 통합 배열 =====
export const ALL_DISTRICTS: District[] = [
  ...SEOUL_DISTRICTS,
  ...BUSAN_DISTRICTS,
  ...JEJU_DISTRICTS,
];

export const ALL_LANDMARKS: Landmark[] = [
  ...SEOUL_LANDMARKS,
  ...BUSAN_LANDMARKS,
];

// ===== 헬퍼 함수 =====

/**
 * 도시 ID로 해당 도시의 구/군 목록 가져오기
 */
export function getDistrictsByCityId(cityId: string): District[] {
  return ALL_DISTRICTS.filter((d) => d.cityId === cityId);
}

/**
 * 도시 ID로 해당 도시의 랜드마크 목록 가져오기
 */
export function getLandmarksByCityId(cityId: string): Landmark[] {
  return ALL_LANDMARKS.filter((l) => l.cityId === cityId);
}

/**
 * 이름 또는 별칭으로 구/군 찾기 (다국어 지원)
 */
export function findDistrictByName(
  name: string,
  locale: Locale = "ko"
): District | undefined {
  const lowerName = name.toLowerCase().trim();

  return ALL_DISTRICTS.find((d) => {
    // 정확한 이름 매칭 (locale 우선)
    if (d.name[locale]?.toLowerCase() === lowerName) return true;
    if (d.name.ko?.toLowerCase() === lowerName) return true;
    if (d.name.en?.toLowerCase() === lowerName) return true;

    // 별칭 매칭
    if (d.aliases?.some((a) => a.toLowerCase() === lowerName)) return true;

    return false;
  });
}

/**
 * 이름 또는 별칭으로 랜드마크 찾기 (다국어 지원)
 */
export function findLandmarkByName(
  name: string,
  locale: Locale = "ko"
): Landmark | undefined {
  const lowerName = name.toLowerCase().trim();

  return ALL_LANDMARKS.find((l) => {
    // 정확한 이름 매칭 (locale 우선)
    if (l.name[locale]?.toLowerCase() === lowerName) return true;
    if (l.name.ko?.toLowerCase() === lowerName) return true;
    if (l.name.en?.toLowerCase() === lowerName) return true;

    // 별칭 매칭
    if (l.aliases?.some((a) => a.toLowerCase() === lowerName)) return true;

    return false;
  });
}

/**
 * 이름으로 구/군 또는 랜드마크 찾기 (통합)
 * 랜드마크 우선 → 구/군 순서
 */
export function findLocationByName(
  name: string,
  locale: Locale = "ko"
): { type: "landmark" | "district"; data: Landmark | District } | undefined {
  // 랜드마크 우선 검색
  const landmark = findLandmarkByName(name, locale);
  if (landmark) {
    return { type: "landmark", data: landmark };
  }

  // 구/군 검색
  const district = findDistrictByName(name, locale);
  if (district) {
    return { type: "district", data: district };
  }

  return undefined;
}

/**
 * 구/군/랜드마크 검색 bounds 가져오기
 */
export function getLocationBounds(
  name: string,
  locale: Locale = "ko"
): { ne_lat: number; ne_lng: number; sw_lat: number; sw_lng: number } | null {
  const location = findLocationByName(name, locale);
  if (!location) return null;

  const coords = location.data.coordinates;
  const radius =
    location.type === "landmark"
      ? (location.data as Landmark).searchRadius || 2
      : 5;

  // km를 도(degree)로 변환 (대략 111km = 1도)
  const offset = radius / 111;

  return {
    ne_lat: coords.latitude + offset,
    ne_lng: coords.longitude + offset,
    sw_lat: coords.latitude - offset,
    sw_lng: coords.longitude - offset,
  };
}
